import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import "../css/Navbar.css";

function Navbar() {
  const [isAdmin, setIsAdmin] = useState(null); // null = loading, false = not admin, true = admin
  const [isScrolled, setIsScrolled] = useState(false);
  const { cartItems } = useCart();
  const location = useLocation();

  const cartItemsCount = cartItems.reduce(
    (total, item) => total + item.quantity,
    0,
  );

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Check if user is admin
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAdmin(!!user);
    });
    return () => unsubscribe();
  }, []);

  // Handle scroll to make navbar sticky
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Don't show navbar on admin pages for non-admin users
  if (location.pathname.startsWith("/admin") && !isAdmin) {
    return null;
  }

  // Don't render navbar until we know admin status to prevent cart button flash
  if (isAdmin === null) {
    return null;
  }

  return (
    <>
      {/* Top Contact Bar - Always Fixed (Desktop Only) */}
      <div className={`top-bar ${isScrolled ? "top-bar-hidden" : ""}`}>
        <div className="top-bar-container">
          <div className="top-bar-left">
            <a
              href="https://www.instagram.com/luxury_life_stayle/"
              className="top-bar-link"
              target="_blank"
              rel="noopener noreferrer"
              title="Instagram"
            >
              <i className="fab fa-instagram"></i>
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=100087035404500"
              className="top-bar-link"
              target="_blank"
              rel="noopener noreferrer"
              title="Facebook"
            >
              <i className="fab fa-facebook-f"></i>
            </a>
          </div>
          <div className="top-bar-right">
            <a href="tel:+972592806088" className="top-bar-contact">
              <i className="fas fa-phone-alt"></i>
              <span>972592806088+</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navbar - Desktop */}
      <header
        className={`navbar navbar-desktop ${isScrolled ? "navbar-sticky" : ""}`}
      >
        <div className="nav-container">
          <Link to="/" className="nav-logo" aria-label="Luxury Fashion - Home">
            <img
              src="/images/logo.webp"
              alt="Luxury Fashion"
              className="nav-logo-img"
              width="60"
              height="60"
              loading="eager"
              decoding="async"
            />
            <span className="nav-logo-text">Luxury Fashion</span>
          </Link>

          <nav className="nav-menu">
            <Link
              to="/"
              className={`nav-link ${isActive("/") ? "nav-link-active" : ""}`}
            >
              الرئيسية
            </Link>
            <Link
              to="/products"
              className={`nav-link ${
                isActive("/products") ? "nav-link-active" : ""
              }`}
            >
              المنتجات
            </Link>
            <Link
              to="/about"
              className={`nav-link ${
                isActive("/about") ? "nav-link-active" : ""
              }`}
            >
              من نحن
            </Link>
            <Link
              to="/contact"
              className={`nav-link ${
                isActive("/contact") ? "nav-link-active" : ""
              }`}
            >
              اتصل بنا
            </Link>

            {/* Only show cart for non-admin users */}
            {isAdmin === false && (
              <Link
                to="/cart"
                className={`nav-link cart-link ${
                  isActive("/cart") ? "nav-link-active" : ""
                }`}
              >
                <span className="cart-icon">
                  <i className="fas fa-shopping-cart"></i>
                </span>
                {cartItemsCount > 0 && (
                  <span className="cart-badge">{cartItemsCount}</span>
                )}
              </Link>
            )}

            {/* Show admin dashboard link for admin users */}
            {isAdmin === true && (
              <Link
                to="/admin/dashboard"
                className={`nav-link admin-link ${
                  location.pathname.startsWith("/admin")
                    ? "nav-link-active"
                    : ""
                }`}
              >
                لوحة التحكم
              </Link>
            )}
          </nav>
        </div>
      </header>

      {/* Mobile Top Header - Logo Only */}
      <header
        className={`navbar navbar-mobile-top ${isScrolled ? "navbar-sticky" : ""}`}
      >
        <div className="nav-container">
          <Link to="/" className="nav-logo" aria-label="Luxury Fashion - Home">
            <img
              src="/images/logo.webp"
              alt="Luxury Fashion"
              className="nav-logo-img"
            />
            <span className="nav-logo-text">Luxury Fashion</span>
          </Link>
        </div>
      </header>

      {/* Bottom Navigation - Mobile Only */}
      <nav className="bottom-nav">
        <Link
          to="/"
          className={`bottom-nav-item ${isActive("/") ? "bottom-nav-item-active" : ""}`}
        >
          <i className="fas fa-home"></i>
          <span>الرئيسية</span>
        </Link>
        <Link
          to="/products"
          className={`bottom-nav-item ${isActive("/products") ? "bottom-nav-item-active" : ""}`}
        >
          <i className="fas fa-th-large"></i>
          <span>المنتجات</span>
        </Link>

        {/* Cart button for non-admin users */}
        {isAdmin === false && (
          <Link
            to="/cart"
            className={`bottom-nav-item bottom-nav-cart ${isActive("/cart") ? "bottom-nav-item-active" : ""}`}
          >
            <div className="bottom-nav-cart-icon">
              <i className="fas fa-shopping-cart"></i>
              {cartItemsCount > 0 && (
                <span className="bottom-nav-badge">{cartItemsCount}</span>
              )}
            </div>
            <span>السلة</span>
          </Link>
        )}

        {/* Admin dashboard link for admin users */}
        {isAdmin === true && (
          <Link
            to="/admin/dashboard"
            className={`bottom-nav-item ${location.pathname.startsWith("/admin") ? "bottom-nav-item-active" : ""}`}
          >
            <i className="fas fa-cog"></i>
            <span>لوحة التحكم</span>
          </Link>
        )}

        <Link
          to="/about"
          className={`bottom-nav-item ${isActive("/about") ? "bottom-nav-item-active" : ""}`}
        >
          <i className="fas fa-info-circle"></i>
          <span>من نحن</span>
        </Link>
        <Link
          to="/contact"
          className={`bottom-nav-item ${isActive("/contact") ? "bottom-nav-item-active" : ""}`}
        >
          <i className="fas fa-phone-alt"></i>
          <span>اتصل بنا</span>
        </Link>
      </nav>
    </>
  );
}

export default Navbar;
